import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTimer } from '../contexts/TimerContext';
import { useTranslation } from 'react-i18next';
import usePushNotifications from '../hooks/usePushNotifications';
import {
  Home,
  Calendar,
  CheckSquare,
  Timer,
  BarChart3,
  Users,
  LogOut,
  Menu,
  X,
  Languages,
  Bell,
  BellOff,
  UserCircle,
  ClipboardList,
  StickyNote,
  FolderKanban,
  Wallet,
  CalendarClock,
  MessageCircle,
  Vote,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_asunya-staff/artifacts/ih91cek0_IMG_6472.png';

const Layout = ({ children }) => {
  const { user, logout, getAuthHeader } = useAuth();
  const { isRunning, isPaused } = useTimer();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);

  useEffect(() => {
    let active = true;
    const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const poll = async () => {
      try {
        const res = await axios.get(`${API_URL}/chat/unread`, { headers: getAuthHeader() });
        if (active) setChatUnread(res.data?.unread || 0);
      } catch (e) {
        console.error('chat: failed to load unread count', e);
      }
    };
    poll();
    const id = setInterval(poll, 15000);
    return () => { active = false; clearInterval(id); };
  }, [getAuthHeader, location.pathname]);

  const {
    isSupported: pushSupported,
    isSubscribed: pushSubscribed,
    isLoading: pushLoading,
    subscribe: subscribePush,
    unsubscribe: unsubscribePush,
  } = usePushNotifications(getAuthHeader);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
  }, []);

  const handlePushToggle = async () => {
    try {
      if (pushSubscribed) {
        await unsubscribePush();
        toast.success(t('notifications.disabled'));
      } else {
        await subscribePush();
        toast.success(t('notifications.enabled'));
      }
    } catch (error) {
      if (error.message === 'Permission denied') {
        toast.error(t('notifications.permissionDenied'));
      } else {
        toast.error(t('notifications.error'));
      }
    }
  };

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'de' ? 'en' : 'de');
  };

  const primaryNav = [
    { path: '/home', icon: Home, label: t('nav.home') },
    { path: '/plan', icon: ClipboardList, label: t('nav.plan') },
    { path: '/chat', icon: MessageCircle, label: t('nav.chat'), badge: chatUnread },
    { path: '/polls', icon: Vote, label: t('nav.polls') },
    { path: '/projects', icon: FolderKanban, label: t('nav.projects') },
    { path: '/office', icon: Calendar, label: t('nav.office') },
    { path: '/leave', icon: CalendarClock, label: t('nav.leave') },
    { path: '/tasks', icon: CheckSquare, label: t('nav.tasks') },
    { path: '/timer', icon: Timer, label: t('nav.timer') },
    { path: '/notes', icon: StickyNote, label: t('nav.notes') },
    { path: '/weekly', icon: BarChart3, label: t('nav.weekly') },
  ];
  if (user?.role === 'admin') {
    primaryNav.push({ path: '/admin', icon: Users, label: t('nav.admin') });
    primaryNav.push({ path: '/finance', icon: Wallet, label: t('nav.finance') });
  }

  const bottomNavItems = [
    { path: '/home', icon: Home, label: t('nav.home') },
    { path: '/plan', icon: ClipboardList, label: t('nav.plan') },
    { path: '/chat', icon: MessageCircle, label: t('nav.chat'), badge: chatUnread },
    { path: '/leave', icon: CalendarClock, label: t('nav.leaveShort') },
    { path: '/tasks', icon: CheckSquare, label: t('nav.tasks') },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Desktop Sidebar (fixed 240px) */}
      <aside
        className="hidden md:flex fixed inset-y-0 left-0 w-60 bg-white border-r border-zinc-200 flex-col z-40"
        data-testid="desktop-sidebar"
      >
        {/* Brand */}
        <div className="h-16 flex items-center px-5 border-b border-zinc-100">
          <Link to="/home" className="flex items-center gap-2.5" data-testid="sidebar-logo-link">
            <img src={LOGO_URL} alt="asunya" className="h-9 w-auto" />
          </Link>
          {isRunning && (
            <motion.div
              animate={{ scale: [1, 1.08, 1] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className={`ml-auto px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                isPaused ? 'bg-[#FEF3C7] text-[#B45309]' : 'bg-[#FEE2E2] text-[#D93025]'
              }`}
              data-testid="timer-active-indicator"
            >
              {isPaused ? t('timer.paused') : t('timer.running')}
            </motion.div>
          )}
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-3 px-2.5">
          <ul className="space-y-0.5">
            {primaryNav.map((item) => {
              const active = isActive(item.path);
              const showDot = item.path === '/timer' && isRunning;
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    data-testid={`sidebar-nav-${item.path.replace('/', '')}`}
                    className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                      active
                        ? 'bg-[#D93025] text-white font-semibold shadow-sm'
                        : 'text-zinc-700 hover:bg-zinc-100'
                    }`}
                  >
                    <item.icon className={`w-[18px] h-[18px] ${active ? '' : 'text-zinc-500 group-hover:text-zinc-700'}`} />
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.badge > 0 && (
                      <span className={`min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full text-[10px] font-bold ${active ? 'bg-white text-[#D93025]' : 'bg-[#D93025] text-white'}`}>
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                    {showDot && (
                      <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-white' : 'bg-[#D93025]'} animate-pulse`} />
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer utilities */}
        <div className="border-t border-zinc-100 p-2.5 space-y-1">
          {pushSupported && (
            <button
              type="button"
              onClick={handlePushToggle}
              disabled={pushLoading}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors ${
                pushSubscribed ? 'text-[#D93025] bg-[#FEE2E2]/60 hover:bg-[#FEE2E2]' : 'text-zinc-700 hover:bg-zinc-100'
              }`}
              data-testid="push-notification-toggle"
              title={pushSubscribed ? t('notifications.clickToDisable') : t('notifications.clickToEnable')}
            >
              {pushSubscribed ? <Bell className="w-[18px] h-[18px]" /> : <BellOff className="w-[18px] h-[18px] text-zinc-500" />}
              <span className="flex-1 text-left truncate">
                {pushSubscribed ? t('notifications.enabled') : t('notifications.enable')}
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={toggleLanguage}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-zinc-700 hover:bg-zinc-100 transition-colors"
            data-testid="language-switcher"
          >
            <Languages className="w-[18px] h-[18px] text-zinc-500" />
            <span className="flex-1 text-left">{i18n.language === 'de' ? 'English' : 'Deutsch'}</span>
            <span className="text-[10px] font-bold text-zinc-400">{i18n.language === 'de' ? 'EN' : 'DE'}</span>
          </button>
          <Link
            to="/profile"
            data-testid="profile-link"
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors ${
              location.pathname === '/profile' ? 'bg-zinc-900 text-white' : 'text-zinc-700 hover:bg-zinc-100'
            }`}
          >
            <UserCircle className={`w-[18px] h-[18px] ${location.pathname === '/profile' ? '' : 'text-zinc-500'}`} />
            <span className="flex-1 text-left truncate">{user?.name || t('nav.profile')}</span>
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-zinc-600 hover:bg-zinc-100 transition-colors"
          >
            <LogOut className="w-[18px] h-[18px] text-zinc-400" />
            <span className="flex-1 text-left">{t('nav.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <nav className="md:hidden bg-white/85 backdrop-blur-md border-b border-zinc-200 sticky top-0 z-50">
        <div className="px-4 flex items-center justify-between h-16">
          <Link to="/home" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="asunya" className="h-10 w-auto" />
          </Link>
          {isRunning && (
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                isPaused ? 'bg-[#FEF3C7] text-[#B45309]' : 'bg-[#FEE2E2] text-[#D93025]'
              }`}
            >
              {isPaused ? t('timer.paused') : t('timer.running')}
            </motion.div>
          )}
          <button
            type="button"
            onClick={() => setMobileMenuOpen((v) => !v)}
            className="flex items-center"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white border-t border-zinc-200"
            >
              <div className="px-4 py-4 space-y-2">
                <Link
                  to="/weekly"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center px-4 py-3 rounded-xl transition-all ${
                    isActive('/weekly') ? 'bg-[#D93025] text-white' : 'text-zinc-700 hover:bg-zinc-100'
                  }`}
                >
                  <BarChart3 className="w-5 h-5 mr-3" />
                  {t('nav.weekly')}
                </Link>
                <Link
                  to="/office"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center px-4 py-3 rounded-xl transition-all ${
                    isActive('/office') ? 'bg-[#D93025] text-white' : 'text-zinc-700 hover:bg-zinc-100'
                  }`}
                >
                  <Calendar className="w-5 h-5 mr-3" />
                  {t('nav.office')}
                </Link>
                <Link
                  to="/projects"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="nav-projects-link"
                  className={`flex items-center px-4 py-3 rounded-xl transition-all ${
                    isActive('/projects') ? 'bg-[#D93025] text-white' : 'text-zinc-700 hover:bg-zinc-100'
                  }`}
                >
                  <FolderKanban className="w-5 h-5 mr-3" />
                  {t('nav.projects')}
                </Link>
                {user?.role === 'admin' && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center px-4 py-3 rounded-xl transition-all ${
                      isActive('/admin') ? 'bg-[#D93025] text-white' : 'text-zinc-700 hover:bg-zinc-100'
                    }`}
                  >
                    <Users className="w-5 h-5 mr-3" />
                    {t('nav.admin')}
                  </Link>
                )}
                {user?.role === 'admin' && (
                  <Link
                    to="/finance"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="nav-finance-link"
                    className={`flex items-center px-4 py-3 rounded-xl transition-all ${
                      isActive('/finance') ? 'bg-[#D93025] text-white' : 'text-zinc-700 hover:bg-zinc-100'
                    }`}
                  >
                    <Wallet className="w-5 h-5 mr-3" />
                    {t('nav.finance')}
                  </Link>
                )}
                {pushSupported && (
                  <button
                    type="button"
                    onClick={() => { handlePushToggle(); setMobileMenuOpen(false); }}
                    disabled={pushLoading}
                    className={`w-full flex items-center px-4 py-3 rounded-xl transition-all ${
                      pushSubscribed ? 'bg-[#FEE2E2] text-[#D93025]' : 'text-zinc-700 hover:bg-zinc-100'
                    }`}
                  >
                    {pushSubscribed ? <Bell className="w-5 h-5 mr-3" /> : <BellOff className="w-5 h-5 mr-3" />}
                    {pushSubscribed ? t('notifications.disable') : t('notifications.enable')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { toggleLanguage(); setMobileMenuOpen(false); }}
                  className="w-full flex items-center px-4 py-3 rounded-xl text-zinc-700 hover:bg-zinc-100 transition-all"
                >
                  <Languages className="w-5 h-5 mr-3" />
                  {i18n.language === 'de' ? 'English' : 'Deutsch'}
                </button>
                <Link
                  to="/profile"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`w-full flex items-center px-4 py-3 rounded-xl transition-all ${
                    isActive('/profile') ? 'bg-[#D93025] text-white' : 'text-zinc-700 hover:bg-zinc-100'
                  }`}
                >
                  <UserCircle className="w-5 h-5 mr-3" />
                  {t('nav.profile')}
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full flex items-center px-4 py-3 rounded-xl text-zinc-700 hover:bg-zinc-100 transition-all"
                >
                  <LogOut className="w-5 h-5 mr-3" />
                  {t('nav.logout')}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Main content - shifted right on desktop */}
      <main className="md:ml-60 min-h-screen pb-24 md:pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-6 md:py-8">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            {children}
          </motion.div>
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-zinc-200 z-50 safe-area-bottom"
        data-testid="bottom-nav"
      >
        <div className="flex justify-around items-center h-16 px-2">
          {bottomNavItems.map((item) => {
            const active = isActive(item.path);
            const isTimer = item.path === '/timer';
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`bottom-nav-${item.path.replace('/', '')}`}
                className={`flex flex-col items-center justify-center flex-1 py-2 rounded-xl transition-all ${
                  active ? 'text-[#D93025]' : 'text-zinc-500 hover:text-zinc-700'
                }`}
              >
                <div className={`relative ${isTimer && isRunning ? 'animate-pulse' : ''}`}>
                  <item.icon className={`w-6 h-6 ${active ? 'stroke-[2.5px]' : ''}`} />
                  {isTimer && isRunning && (
                    <motion.div
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ repeat: Infinity, duration: 1.5 }}
                      className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#D93025] rounded-full"
                    />
                  )}
                  {item.badge > 0 && (
                    <span className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-full text-[9px] font-bold bg-[#D93025] text-white">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </div>
                <span className={`text-xs mt-1 ${active ? 'font-semibold' : 'font-medium'}`}>
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default Layout;
